import React, { useState, useRef, useEffect } from 'react';

/**
 * VAS Slider Component - eCOA Consortium Compliant
 * 
 * Implements Visual Analog Scale according to FDA PRO Guidance and eCOA standards:
 * - Fixed length: 100mm (10cm) as per FDA PRO Guidance
 * - Verbal anchors at extreme ends ONLY
 * - No descriptors at intermediate positions
 * - No numbers at anchor positions
 * - Consistent size across all devices
 * 
 * Reference: eCOA Consortium Response Scales Documentation
 * 
 */
const VASSlider = ({
  leftAnchor = "No pain",
  rightAnchor = "Pain as bad as you can imagine",
  value = 50,
  onChange,
  disabled = false,
  showValue = false, // For design mode only, not for actual survey
  showCurrentValue = false, // Show selected value under the slider thumb (configurable per question)
  hideMetadata = false, // Hide eCOA compliance metadata (for preview/participant view)
  colorGradient = false, // Apply a green → yellow → red gradient along the track
  className = ""
}) => {
  const [currentValue, setCurrentValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);
  const trackRef = useRef(null);

  // CRITICAL: Fixed 100mm (10cm) length as per eCOA standards
  // Using CSS pixels: 1mm ≈ 3.7795px at 96 DPI
  // 100mm = 377.95px ≈ 378px
  const FIXED_LENGTH_PX = 378; // 100mm in pixels
  const FIXED_HEIGHT_PX = 60; // Height for the slider track
  
  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handlePositionChange = (clientX) => {
    if (disabled || !trackRef.current) return;

    const rect = trackRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    setCurrentValue(percentage);
    if (onChange) {
      onChange(percentage);
    }
  };

  const handleMouseDown = (e) => {
    if (disabled) return;
    setIsDragging(true);
    handlePositionChange(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (isDragging && !disabled) {
      handlePositionChange(e.clientX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (disabled) return;
    setIsDragging(true);
    handlePositionChange(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (isDragging && !disabled) {
      e.preventDefault();
      handlePositionChange(e.touches[0].clientX);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging]);

  return (
    <div 
      ref={sliderRef}
      className={`vas-slider-container flex flex-col items-center ${className}`}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none',
        overflow: 'visible'
      }}
    >
      {/* Compliance Notice (hidden in production) */}
      {!hideMetadata && process.env.NODE_ENV === 'development' && (
        <div className="text-xs text-gray-400 mb-2 font-mono">
          VAS: 100mm fixed length (eCOA compliant)
        </div>
      )}

      {/* VAS Slider Track - FIXED 100mm LENGTH */}
      <div className="relative flex items-center justify-center mb-4">
        <div
          ref={trackRef}
          className={`relative border-2 border-gray-400 ${
            colorGradient ? '' : 'bg-gray-200 dark:bg-gray-700'
          } ${
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          }`}
          style={{
            width: `${FIXED_LENGTH_PX}px`, // FIXED: 100mm = 378px
            height: `${FIXED_HEIGHT_PX}px`,
            minWidth: `${FIXED_LENGTH_PX}px`, // Prevent responsive resizing
            maxWidth: `${FIXED_LENGTH_PX}px`,  // Enforce fixed width
            borderRadius: '4px',
            overflow: 'visible',
            ...(colorGradient
              ? { background: 'linear-gradient(to right, hsl(120, 70%, 55%) 0%, hsl(60, 85%, 55%) 50%, hsl(0, 75%, 55%) 100%)' }
              : {})
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Filled portion (optional visual feedback) - hidden when gradient is on so colors stay visible */}
          {!colorGradient && (
            <div
              className="absolute top-0 left-0 h-full bg-blue-100 transition-all duration-100"
              style={{
                width: `${currentValue}%`,
                borderRadius: '2px 0 0 2px',
                pointerEvents: 'none'
              }}
            />
          )}

          {/* Draggable Marker */}
          <div
            className={`absolute top-1/2 -translate-y-1/2 w-8 h-16 bg-white dark:bg-gray-800 border-3 border-blue-600 rounded-lg shadow-lg dark:shadow-gray-900/20 transition-all duration-100 ${
              isDragging ? 'scale-110' : ''
            }`}
            style={{
              left: `calc(${currentValue}% - 16px)`, // 16px = half of marker width
              cursor: disabled ? 'not-allowed' : 'grab',
              pointerEvents: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}
          >
            {/* Marker handle lines */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="space-y-1">
                <div className="w-4 h-0.5 bg-blue-600"></div>
                <div className="w-4 h-0.5 bg-blue-600"></div>
                <div className="w-4 h-0.5 bg-blue-600"></div>
              </div>
            </div>
          </div>

          {/* Current Value Display - positioned under the thumb */}
          {showCurrentValue && (
            <div
              className="absolute text-sm font-bold text-blue-700 select-none"
              style={{
                left: `${currentValue}%`,
                top: `${FIXED_HEIGHT_PX + 4}px`,
                transform: 'translateX(-50%)',
                pointerEvents: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              {Math.round(currentValue)}
            </div>
          )}
        </div>
      </div>

      {/* Anchors - ONLY at extreme ends, NO intermediate labels */}
      <div
        style={{
          width: `${FIXED_LENGTH_PX}px`,
          minWidth: `${FIXED_LENGTH_PX}px`,
          maxWidth: `${FIXED_LENGTH_PX}px`,
          position: 'relative',
          overflow: 'visible'
        }}
      >
        {/* Tick marks row - at exact 0% and 100% edges */}
        <div className="flex justify-between">
          <div className="w-0.5 h-3 bg-gray-400"></div>
          <div className="w-0.5 h-3 bg-gray-400"></div>
        </div>

        {/* Labels row - left label right-aligned to left tick, right label left-aligned to right tick */}
        <div className="relative mt-1 mb-8" style={{ overflow: 'visible' }}>
          {/* Left label: right edge aligns with left tick */}
          <span
            className="absolute text-sm text-gray-700 font-medium leading-tight"
            style={{
              right: `calc(100% - 1px)`,
              top: 0,
              textAlign: 'right',
              whiteSpace: 'nowrap'
            }}
          >
            {leftAnchor}
          </span>
          {/* Right label: left edge aligns with right tick */}
          <span
            className="absolute text-sm text-gray-700 font-medium leading-tight"
            style={{
              left: `calc(100% - 1px)`,
              top: 0,
              textAlign: 'left',
              whiteSpace: 'nowrap'
            }}
          >
            {rightAnchor}
          </span>
          {/* Spacer to reserve vertical space for labels */}
          <div className="invisible text-sm leading-tight whitespace-nowrap">&nbsp;</div>
        </div>
      </div>

      {/* Value Display (for design/testing only - NOT displayed in actual surveys) */}
      {showValue && (
        <div className="mt-3 px-3 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 rounded text-xs text-gray-600 font-mono">
          Value: {currentValue.toFixed(1)}
        </div>
      )}

      {/* Compliance Info */}
      {!hideMetadata && (
        <div className="mt-8 text-xs text-gray-500 text-center max-w-md">
          <p className="italic">
            eCOA Compliant: Fixed 100mm (10cm) line length with verbal anchors at endpoints only.
          </p>
        </div>
      )}
    </div>
  );
};

export default VASSlider;
